import { StateModel, StateModuleInfo } from '../models/state.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getMenus, getModules } from '../../api/resource';
import {
  moduleMenuItemToTreeNode,
  moduleResponseToTreeNode,
} from '../../utils/moduleResponseToTreeNode';
import { SidebarTreeNode } from '../../models/SidebarTreeNode';

const initialState: StateModuleInfo = {
  loadedModules: [],
  modulesTreeData: [],
  isModulesLoading: false,
};

export const fetchModules = createAsyncThunk(
  'base/modules/modules',
  async () => {
    const res = await getModules();
    return res.data;
  },
  {
    condition: (args, { getState }) => {
      const { modules } = getState() as StateModel;
      return !modules.isModulesLoading;
    },
  },
);

type FetchModuleChildrenArgs = {
  moduleName: string;
  parentId: number;
  parentNode: SidebarTreeNode;
};

export const fetchModuleChildren = createAsyncThunk(
  'modules/menuItems',
  async ({ moduleName, parentId }: FetchModuleChildrenArgs, { rejectWithValue }) => {
    try {
      const res = await getMenus(moduleName, parentId);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

function addChildrenToNode(
  nodes: Array<SidebarTreeNode>,
  targetKey: string,
  newChildren: Array<SidebarTreeNode>,
): Array<SidebarTreeNode> {
  return nodes.map((node) => {
    if (node.key === targetKey) {
      return {
        ...node,
        children: newChildren,
      };
    }

    if (node.children) {
      return {
        ...node,
        children: addChildrenToNode(node.children, targetKey, newChildren),
      };
    }

    return node;
  });
}

const modulesSlice = createSlice({
  name: 'modules',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchModules.pending, (state: StateModuleInfo) => {
      state.isModulesLoading = true;
    });
    builder.addCase(fetchModules.fulfilled, (state: StateModuleInfo, action) => {
      state.loadedModules = action.payload;
      state.modulesTreeData = moduleResponseToTreeNode(action.payload);
      state.isModulesLoading = false;
    });
    builder.addCase(fetchModules.rejected, (state: StateModuleInfo) => {
      state.loadedModules = [];
      state.modulesTreeData = [];
      state.isModulesLoading = false;
    });
    builder.addCase(fetchModuleChildren.fulfilled, (state, action) => {
      const { parentNode } = action.meta.arg;
      const newChildren = moduleMenuItemToTreeNode(parentNode!, action.payload);
      state.modulesTreeData = addChildrenToNode(state.modulesTreeData, parentNode.key, newChildren);
    });
  },
});

const ModulesReducer = modulesSlice.reducer;

export default ModulesReducer;